import { useEffect, useState, memo } from 'react'
import { useParams, Navigate, useNavigate } from 'react-router-dom'
// (no Link import — nav uses useNavigate to avoid invalid <a><button> nesting)
import { useTranslation } from 'react-i18next'
import {
  HeartHandshake, Building2, Briefcase, UtensilsCrossed,
  Handshake, Users, MapPin, Stethoscope, ShieldCheck, BookOpen,
  ExternalLink, Phone, FileText, Clock, ArrowRight,
} from 'lucide-react'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { useAuthStore } from '@/stores/authStore'
import {
  getMySupporterRegistrations,
  REGISTRATION_TYPE_LABELS,
  SUPPORTER_STATUS_LABELS,
  type SupporterRegistration,
} from '@/services/supporterService'
import { SUPPORTER_SLUGS, type SupporterSlug } from '@/utils/userTypeDestination'
// Redesigned role-specific dashboards (non-financial, in-page section nav).
// Only the four primary supporter personas get the new dashboard; `volunteer`
// still redirects to /community-dashboard. The CSR-partner persona was retired.
import IndividualSupporterDashboard from './supporter/IndividualSupporterDashboard'
import NgoSupporterDashboard from './supporter/NgoSupporterDashboard'
import CorporateSupporterDashboard from './supporter/CorporateSupporterDashboard'
import NutritionSponsorDashboard from './supporter/NutritionSponsorDashboard'

// ================================================================================
// PER-TYPE CONFIG — one adaptive dashboard, adapts hero + sections per slug.
// registrationType maps to the public SupporterRegistration.RegistrationType so the
// "start registration" CTA deep-links into the Support-a-Patient wizard pre-selected.
// ================================================================================
type IconType = React.ComponentType<{ className?: string }>
interface QuickAction {
  key: string
  path: string
  icon: IconType
}
interface SupporterConfig {
  userType: string
  registrationType: string
  titleKey: string
  roleKey: string
  aboutKey: string
  icon: IconType
  accent: string // hero icon tile gradient
  quickActions: QuickAction[]
}

const SUPPORTER_CONFIG: Record<SupporterSlug, SupporterConfig> = {
  individual: {
    userType: 'SUPPORTER_INDIVIDUAL',
    registrationType: 'NIKSHAY_MITRA_INDIVIDUAL',
    titleKey: 'dashboard.supporter.titleIndividual',
    roleKey: 'dashboard.supporter.roleIndividual',
    aboutKey: 'dashboard.supporter.aboutIndividual',
    icon: HeartHandshake,
    accent: 'bg-gradient-to-br from-sky-500 to-blue-600',
    quickActions: [
      { key: 'dashboard.supporter.actionBecomeSupporter', path: '/become-a-supporter', icon: HeartHandshake },
      { key: 'dashboard.supporter.actionSupportAPatient', path: '/support-a-patient?type=NIKSHAY_MITRA_INDIVIDUAL&register=1', icon: Handshake },
      { key: 'dashboard.supporter.actionFindCentre', path: '/find-tb-centre', icon: MapPin },
      { key: 'dashboard.supporter.actionSymptomChecker', path: '/symptom-checker', icon: Stethoscope },
    ],
  },
  ngo: {
    userType: 'SUPPORTER_NGO',
    registrationType: 'NIKSHAY_MITRA_NGO',
    titleKey: 'dashboard.supporter.titleNgo',
    roleKey: 'dashboard.supporter.roleNgo',
    aboutKey: 'dashboard.supporter.aboutNgo',
    icon: Building2,
    accent: 'bg-gradient-to-br from-indigo-500 to-blue-600',
    quickActions: [
      { key: 'dashboard.supporter.actionBecomeSupporter', path: '/become-a-supporter', icon: Building2 },
      { key: 'dashboard.supporter.actionSupportAPatient', path: '/support-a-patient?type=NIKSHAY_MITRA_NGO&register=1', icon: Handshake },
      { key: 'dashboard.supporter.actionFindCentre', path: '/find-tb-centre', icon: MapPin },
      { key: 'dashboard.supporter.actionKnowledge', path: '/knowledge', icon: BookOpen },
    ],
  },
  corporate: {
    userType: 'SUPPORTER_CORPORATE',
    registrationType: 'NIKSHAY_MITRA_CORPORATE',
    titleKey: 'dashboard.supporter.titleCorporate',
    roleKey: 'dashboard.supporter.roleCorporate',
    aboutKey: 'dashboard.supporter.aboutCorporate',
    icon: Briefcase,
    accent: 'bg-gradient-to-br from-violet-500 to-indigo-600',
    quickActions: [
      { key: 'dashboard.supporter.actionBecomeSupporter', path: '/become-a-supporter', icon: Briefcase },
      { key: 'dashboard.supporter.actionSupportAPatient', path: '/support-a-patient?type=NIKSHAY_MITRA_CORPORATE&register=1', icon: Handshake },
      { key: 'dashboard.supporter.actionFindCentre', path: '/find-tb-centre', icon: MapPin },
      { key: 'dashboard.supporter.actionKnowledge', path: '/knowledge', icon: BookOpen },
    ],
  },
  nutrition: {
    userType: 'NUTRITION_SPONSOR',
    registrationType: 'NUTRITION_SPONSOR',
    titleKey: 'dashboard.supporter.titleNutrition',
    roleKey: 'dashboard.supporter.roleNutrition',
    aboutKey: 'dashboard.supporter.aboutNutrition',
    icon: UtensilsCrossed,
    accent: 'bg-gradient-to-br from-amber-500 to-orange-600',
    quickActions: [
      { key: 'dashboard.supporter.actionSupportAPatient', path: '/support-a-patient?type=NUTRITION_SPONSOR&register=1', icon: UtensilsCrossed },
      { key: 'dashboard.supporter.actionBecomeSupporter', path: '/become-a-supporter', icon: HeartHandshake },
      { key: 'dashboard.supporter.actionFindCentre', path: '/find-tb-centre', icon: MapPin },
      { key: 'dashboard.supporter.actionKnowledge', path: '/knowledge', icon: BookOpen },
    ],
  },
  volunteer: {
    userType: 'VOLUNTEER_COMMUNITY',
    registrationType: 'VOLUNTEER',
    titleKey: 'dashboard.supporter.titleVolunteer',
    roleKey: 'dashboard.supporter.roleVolunteer',
    aboutKey: 'dashboard.supporter.aboutVolunteer',
    icon: Users,
    accent: 'bg-gradient-to-br from-rose-500 to-pink-600',
    quickActions: [
      { key: 'dashboard.supporter.actionBecomeSupporter', path: '/become-a-supporter', icon: Users },
      { key: 'dashboard.supporter.actionSupportAPatient', path: '/support-a-patient?type=VOLUNTEER&register=1', icon: Handshake },
      { key: 'dashboard.supporter.actionFindCentre', path: '/find-tb-centre', icon: MapPin },
      { key: 'dashboard.supporter.actionAmIAtRisk', path: '/am-i-at-risk', icon: ShieldCheck },
    ],
  },
}

const STATUS_STYLE: Record<string, string> = {
  NEW: 'bg-gray-100 text-gray-700',
  UNDER_REVIEW: 'bg-amber-100 text-amber-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  REJECTED: 'bg-red-100 text-red-700',
  ONBOARDED: 'bg-green-100 text-green-700',
}

// ================================================================================
// MAIN — one adaptive supporter dashboard
// ================================================================================
const SupporterDashboard = () => {
  const { type } = useParams<{ type: string }>()
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const navigate = useNavigate()

  // Invalid slug → bounce to feed rather than render a broken page.
  if (!type || !SUPPORTER_SLUGS.includes(type as SupporterSlug)) {
    return <Navigate to="/feed" replace />
  }
  // VOLUNTEER_COMMUNITY now has its own dedicated /community-dashboard shell.
  // Redirect the legacy 'volunteer' slug there (back-compat for old links).
  if (type === 'volunteer') {
    return <Navigate to="/community-dashboard" replace />
  }
  // The four primary supporter personas render their redesigned role dashboard.
  if (type === 'individual') return <IndividualSupporterDashboard />
  if (type === 'ngo') return <NgoSupporterDashboard />
  if (type === 'corporate') return <CorporateSupporterDashboard />
  if (type === 'nutrition') return <NutritionSponsorDashboard />
  const config = SUPPORTER_CONFIG[type as SupporterSlug]
  const Icon = config.icon

  const [registrations, setRegistrations] = useState<SupporterRegistration[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    setLoading(true)
    getMySupporterRegistrations()
      .then((regs) => { if (active) setRegistrations(regs) })
      .catch((err) => {
        // 401 → caller will be redirected by the route guard; otherwise just show empty.
        if (err?.response?.status !== 401 && active) setRegistrations([])
      })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [config.userType])

  const fullName = user?.fullName || (user ? `${user.firstName} ${user.lastName}`.trim() : '')

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-6">
        <GovPageTitle
          title={t(config.titleKey)}
          subtitle={t('dashboard.supporter.greeting', { name: fullName || '—' })}
          icon={config.icon}
        />

        {/* Hero / about your role */}
        <GovCard>
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${config.accent}`}>
              <Icon className="w-7 h-7 text-white" />
            </div>
            <div>
              <span className="inline-block text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full mb-1">
                {t(config.roleKey)}
              </span>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('dashboard.supporter.aboutTitle')}</h2>
              <p className="text-sm text-gray-600 leading-relaxed">{t(config.aboutKey)}</p>
            </div>
          </div>
        </GovCard>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left — my support journey (real data from /supporters/me) */}
          <div className="lg:col-span-2 space-y-6">
            <GovCard>
              <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.supporter.journeyTitle')}</h3>
              <p className="text-sm text-gray-500 mb-4">{t('dashboard.supporter.journeySubtitle')}</p>

              {loading ? (
                <div className="space-y-3">
                  {[...Array(2)].map((_, i) => (
                    <div key={i} className="h-16 rounded-xl bg-gray-100 animate-pulse" />
                  ))}
                </div>
              ) : registrations.length === 0 ? (
                <GovEmptyState
                  icon="📋"
                  title={t('dashboard.supporter.noRegistrations')}
                  description={t('dashboard.supporter.noRegistrationsDesc')}
                />
              ) : (
                <div className="space-y-3">
                  {registrations.map((reg) => {
                    const statusLabel = reg.status ? (SUPPORTER_STATUS_LABELS[reg.status] ?? reg.status) : '—'
                    const typeLabel = reg.registrationType ? (REGISTRATION_TYPE_LABELS[reg.registrationType] ?? reg.registrationType) : '—'
                    return (
                      <div key={reg.id} className="flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                        <div className="min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">{reg.name}</h4>
                          <p className="text-sm text-gray-500 truncate">
                            {t('dashboard.supporter.regType')}: {typeLabel}
                          </p>
                          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {t('dashboard.supporter.regDate')}: {reg.createdAt ? new Date(reg.createdAt).toLocaleDateString('en-IN') : '—'}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full shrink-0 ml-3 ${STATUS_STYLE[reg.status ?? ''] ?? 'bg-gray-100 text-gray-700'}`}>
                          {statusLabel}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}

              {registrations.length === 0 && !loading && (
                <div className="mt-4">
                  <GovButton
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(`/support-a-patient?type=${encodeURIComponent(config.registrationType)}&register=1`)}
                  >
                    {t('dashboard.supporter.startRegistration')}
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </GovButton>
                </div>
              )}
            </GovCard>
          </div>

          {/* Right — quick actions + official resources */}
          <div className="space-y-6">
            <GovCard>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard.supporter.quickActions')}</h3>
              <div className="space-y-2">
                {config.quickActions.map((action) => {
                  const ActionIcon = action.icon
                  return (
                    <GovButton
                      key={action.key}
                      variant="secondary"
                      className="w-full justify-start"
                      onClick={() => navigate(action.path)}
                    >
                      <ActionIcon className="w-4 h-4 mr-2" />
                      {t(action.key)}
                    </GovButton>
                  )
                })}
              </div>
            </GovCard>

            <GovCard>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('dashboard.supporter.resourcesTitle')}</h3>
              <p className="text-sm text-gray-500 mb-4">{t('dashboard.supporter.resourcesDesc')}</p>
              <div className="space-y-2">
                <a href="https://tbcindia.mohfw.gov.in" target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                  <span className="flex items-center gap-2 text-sm text-gray-700">
                    <FileText className="w-4 h-4 text-blue-600" />
                    {t('dashboard.supporter.officialPortal')}
                  </span>
                  <ExternalLink className="w-4 h-4 text-gray-400" />
                </a>
                <a href="https://nikshay.in" target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                  <span className="flex items-center gap-2 text-sm text-gray-700">
                    <FileText className="w-4 h-4 text-cyan-600" />
                    {t('dashboard.supporter.nikshayPortal')}
                  </span>
                  <ExternalLink className="w-4 h-4 text-gray-400" />
                </a>
                <a href="tel:1800116666"
                  className="flex items-center justify-between p-3 rounded-xl bg-green-50 hover:bg-green-100 transition-colors">
                  <span className="flex items-center gap-2 text-sm text-gray-700">
                    <Phone className="w-4 h-4 text-green-600" />
                    {t('dashboard.supporter.tbHelpline')}
                  </span>
                  <Phone className="w-4 h-4 text-green-500" />
                </a>
              </div>
            </GovCard>
          </div>
        </div>
      </div>
    </GovLayout>
  )
}

export default memo(SupporterDashboard)